/**
 * TestQueuePage (iter145) — the dedicated review queue for the new
 * Test Admin role.
 *
 * Surfaces three sources of changes:
 *   • suggestion — improvement_suggestions in built_pending_deploy
 *   • iteration — published changelog iterations (engineering changes)
 *   • manual    — super-admin-entered one-off items (deletions etc.)
 *
 * Workflow per row:
 *   pending → in_review → approved
 *                     ↘ needs_improvement (auto-creates a child improvement_suggestion)
 *
 * Approval is REQUIRED (alongside manuals_updated) before a suggestion
 * can be deployed — see /api/admin/suggestions/deploy-readiness.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ClipboardText, ArrowsClockwise, Plus, Check, X, Sparkle, Trash, Lightning, FileText, Wrench } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import AdminPageHeader from "@/components/AdminPageHeader";
import QueueTourOverlay from "@/components/QueueTourOverlay";

const STATUS_CHIPS = [
  { v: "all",               label: "All" },
  { v: "pending",           label: "Pending"  },
  { v: "in_review",         label: "In review" },
  { v: "needs_improvement", label: "Needs work" },
  { v: "approved",          label: "Approved" },
];

const SOURCE_BADGE = {
  suggestion: { Icon: Lightning, color: "text-amber-700 bg-amber-50 border-amber-200", label: "User request" },
  iteration:  { Icon: FileText,  color: "text-indigo-700 bg-indigo-50 border-indigo-200", label: "Iteration" },
  manual:     { Icon: Wrench,    color: "text-slate-700 bg-slate-100 border-slate-300", label: "Manual" },
};

const SEVERITY_RIBBON = {
  critical: { color: "bg-rose-500",   label: "Critical" },
  moderate: { color: "bg-amber-400",  label: "Moderate" },
  polish:   { color: "bg-slate-300",  label: "Polish"   },
};

export default function TestQueuePage({ user, onLogout, onLogoutAll }) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get("status") || "all";
  const setFilter = (v) => {
    const next = new URLSearchParams(searchParams);
    if (v === "all") next.delete("status"); else next.set("status", v);
    setSearchParams(next, { replace: true });
  };
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ title: "", body: "", preview_url: "" });
  const isSuper = !!user?.is_super_admin;

  const load = async () => {
    setLoading(true);
    try {
      const url = "/admin/test-queue" + (filter !== "all" ? `?status=${filter}` : "");
      const r = await api.get(url);
      setItems(r.data?.items || []);
      setCounts(r.data?.counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load test queue");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (item, newStatus, note = "") => {
    setBusyId(item.item_id);
    try {
      await api.put(`/admin/test-queue/${encodeURIComponent(item.item_id)}/status`,
                    { status: newStatus, note });
      toast.success(newStatus === "approved" ? "Approved." : `Set to ${newStatus}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status update failed");
    } finally { setBusyId(null); }
  };

  const addComment = async (item) => {
    const body = window.prompt("Improvement comment (will also be filed as a child improvement request):");
    if (!body || body.trim().length < 3) return;
    setBusyId(item.item_id);
    try {
      await api.post(`/admin/test-queue/${encodeURIComponent(item.item_id)}/comment`,
                     { body, create_improvement_request: true });
      toast.success("Comment + child improvement request filed.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Comment failed");
    } finally { setBusyId(null); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete this test review item?\n${item.title}`)) return;
    try {
      await api.delete(`/admin/test-queue/${encodeURIComponent(item.item_id)}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const sync = async (kind) => {
    try {
      const r = await api.post(`/admin/test-queue/sync-${kind}`);
      toast.success(`Backfill: ${r.data?.created ?? 0} new item(s) from ${r.data?.scanned ?? 0} ${kind}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Sync ${kind} failed`);
    }
  };

  const createManual = async () => {
    if (!draft.title || draft.title.length < 2) {
      toast.error("Title needs at least 2 characters."); return;
    }
    try {
      await api.post("/admin/test-queue", draft);
      toast.success("Manual item added");
      setCreating(false);
      setDraft({ title: "", body: "", preview_url: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-['Lato'] text-slate-900" data-testid="test-queue-page">
      <AdminPageHeader
        sticky maxWidth="max-w-5xl" testId="test-queue-back"
        icon={<ClipboardText size={20} weight="fill" className="text-emerald-700" />}
        title="Test Admin queue"
        subtitle="Review every change before deploy. Approve or file improvement requests."
        user={user} onLogout={onLogout} onLogoutAll={onLogoutAll}
      />
      <main className="max-w-5xl mx-auto p-6 space-y-4">
        <QueueTourOverlay />
        {/* Filter chips */}
        <div className="flex flex-wrap items-center gap-2" data-testid="test-queue-filters">
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Filter</span>
          {STATUS_CHIPS.map((c) => (
            <button key={c.v}
                    data-testid={`test-queue-filter-${c.v}`}
                    onClick={() => setFilter(c.v)}
                    className={`px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider rounded-sm border transition ${filter === c.v ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}>
              {c.label} {c.v !== "all" ? `(${counts[c.v] ?? 0})` : `(${counts.total ?? 0})`}
            </button>
          ))}
          <span className="ml-auto flex items-center gap-2">
            {isSuper && (
              <>
                <button onClick={() => sync("suggestions")}
                        data-testid="test-queue-sync-suggestions"
                        className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-amber-300 text-amber-800 hover:bg-amber-50">
                  Sync user requests
                </button>
                <button onClick={() => sync("iterations")}
                        data-testid="test-queue-sync-iterations"
                        className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider rounded-sm border border-indigo-300 text-indigo-800 hover:bg-indigo-50">
                  Sync iterations
                </button>
                <button onClick={() => setCreating(true)}
                        data-testid="test-queue-add-manual"
                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm bg-emerald-600 text-white hover:bg-emerald-700">
                  <Plus size={11} weight="bold" /> Add item
                </button>
              </>
            )}
            <button onClick={load}
                    data-testid="test-queue-refresh"
                    className="inline-flex items-center gap-1.5 px-2 py-2 text-xs border border-slate-200 rounded-sm hover:bg-white">
              <ArrowsClockwise size={11} weight="bold" />
            </button>
          </span>
        </div>

        {loading && (
          <div className="flex items-center justify-center gap-2 py-12 text-slate-500" data-testid="test-queue-loading">
            <ArrowsClockwise size={14} weight="bold" className="animate-spin" />
            <span className="text-sm">Loading…</span>
          </div>
        )}

        {!loading && items.length === 0 && (
          <p className="text-center text-sm text-slate-500 italic py-12" data-testid="test-queue-empty">
            No items in this filter. {isSuper && "Try syncing or adding a manual item above."}
          </p>
        )}

        <ul className="space-y-3" data-testid="test-queue-list">
          {items.map((it) => {
            const badge = SOURCE_BADGE[it.source_type] || SOURCE_BADGE.manual;
            const SourceIcon = badge.Icon;
            const sev = SEVERITY_RIBBON[it.severity] || SEVERITY_RIBBON.moderate;
            const isApproved = it.status === "approved";
            const isNeedsWork = it.status === "needs_improvement";
            return (
              <li key={it.item_id}
                  className="relative bg-white border border-slate-200 rounded-md p-4 pl-5 shadow-sm overflow-hidden"
                  data-testid={`test-queue-item-${it.item_id}`}>
                <span title={`Severity: ${sev.label}`}
                      data-testid={`test-queue-severity-${it.item_id}`}
                      className={`absolute left-0 top-0 bottom-0 w-1.5 ${sev.color}`}></span>
                <div className="flex flex-wrap items-baseline gap-2 mb-1">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${badge.color}`}>
                    <SourceIcon size={9} weight="fill" /> {badge.label}
                  </span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm text-white ${sev.color}`}>
                    {sev.label}
                  </span>
                  <h3 className="text-sm font-bold flex-1 min-w-0 truncate">{it.title}</h3>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${
                    isApproved   ? "bg-emerald-100 text-emerald-800" :
                    isNeedsWork  ? "bg-rose-100    text-rose-800"    :
                    it.status === "in_review" ? "bg-blue-100 text-blue-800" :
                                                 "bg-slate-100 text-slate-700"}`}>
                    {it.status.replace("_", " ")}
                  </span>
                </div>
                {it.body && <p className="text-xs text-slate-700 leading-relaxed mb-2 whitespace-pre-line">{it.body}</p>}
                {it.preview_url && (
                  <a href={it.preview_url} target="_blank" rel="noopener noreferrer"
                     data-testid={`test-queue-preview-${it.item_id}`}
                     className="text-[11px] text-blue-700 hover:underline inline-block mb-2">
                    Open preview ↗
                  </a>
                )}
                {(it.comments || []).length > 0 && (
                  <ul className="text-[11px] text-slate-600 italic space-y-0.5 mt-2 mb-2 pl-3 border-l-2 border-slate-200">
                    {it.comments.slice(-3).map((c) => (
                      <li key={c.comment_id}><strong>{c.author_email}:</strong> {c.body}</li>
                    ))}
                    {it.comments.length > 3 && <li className="text-slate-400 not-italic">+ {it.comments.length - 3} earlier comment(s)</li>}
                  </ul>
                )}
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 flex-wrap">
                  {!isApproved && it.status !== "in_review" && (
                    <button onClick={() => setStatus(it, "in_review")}
                            data-testid={`test-queue-start-${it.item_id}`}
                            disabled={busyId === it.item_id}
                            className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-blue-300 text-blue-700 rounded-sm hover:bg-blue-50">
                      Start review
                    </button>
                  )}
                  {!isApproved && (
                    <button onClick={() => setStatus(it, "approved")}
                            data-testid={`test-queue-approve-${it.item_id}`}
                            disabled={busyId === it.item_id}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 disabled:opacity-50">
                      <Check size={11} weight="bold" /> Approve
                    </button>
                  )}
                  {!isApproved && (
                    <button onClick={() => setStatus(it, "needs_improvement")}
                            data-testid={`test-queue-needswork-${it.item_id}`}
                            disabled={busyId === it.item_id}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-rose-300 text-rose-700 rounded-sm hover:bg-rose-50">
                      <X size={11} weight="bold" /> Needs work
                    </button>
                  )}
                  <button onClick={() => addComment(it)}
                          data-testid={`test-queue-comment-${it.item_id}`}
                          disabled={busyId === it.item_id}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-amber-300 text-amber-800 rounded-sm hover:bg-amber-50">
                    <Sparkle size={11} weight="fill" /> Add improvement
                  </button>
                  {isSuper && (
                    <button onClick={() => remove(it)}
                            data-testid={`test-queue-delete-${it.item_id}`}
                            className="ml-auto px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-rose-700 hover:bg-rose-50 rounded-sm">
                      <Trash size={11} weight="fill" />
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        {creating && (
          <div className="fixed inset-0 z-[400] flex items-start justify-center bg-black/40 p-4 pt-16"
               data-testid="test-queue-create-modal" onClick={() => setCreating(false)}>
            <div className="bg-white border border-slate-200 rounded-md shadow-xl w-full max-w-lg"
                 onClick={(e) => e.stopPropagation()}>
              <header className="flex items-center gap-2 px-5 py-3 border-b border-slate-200">
                <Plus size={16} weight="bold" className="text-emerald-700" />
                <h2 className="text-sm font-bold tracking-tight">Add a test review item</h2>
                <button onClick={() => setCreating(false)} className="ml-auto text-slate-400 hover:text-slate-700">
                  <X size={14} weight="bold" />
                </button>
              </header>
              <div className="p-5 space-y-3">
                <Field label="Title">
                  <input type="text" value={draft.title}
                         data-testid="test-queue-create-title"
                         onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                         className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm" />
                </Field>
                <Field label="Description">
                  <textarea rows={4} value={draft.body}
                            data-testid="test-queue-create-body"
                            onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                            className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm resize-y" />
                </Field>
                <Field label="Preview URL (optional)">
                  <input type="url" value={draft.preview_url}
                         data-testid="test-queue-create-preview"
                         onChange={(e) => setDraft({ ...draft, preview_url: e.target.value })}
                         className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm" />
                </Field>
              </div>
              <footer className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
                <button onClick={() => setCreating(false)}
                        className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-slate-200 rounded-sm">
                  Cancel
                </button>
                <button onClick={createManual}
                        data-testid="test-queue-create-submit"
                        className="px-3 py-2 text-xs font-bold uppercase tracking-wider rounded-sm bg-emerald-600 text-white hover:bg-emerald-700">
                  Add item
                </button>
              </footer>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">{label}</span>
      {children}
    </label>
  );
}
